<template>
    <Paper class="company-list" :header="label">
        <LayoutToggles v-if="withLayoutToggle" slot="header" v-model="isList" />

        <div v-if="chatFilterButton && chatAvailability.length" slot="header">
            <Checkbox id="toggle-chat-companies" v-model="chatFilterToggle" mirror>
                <span class="toggle-text">
                    {{ $t('components.smart.companyModule.showOnlyCompaniesLiveOnChat') }}
                </span>
            </Checkbox>
        </div>

        <div v-if="data.length > 0" :class="{ grid: !isList && !forceList }">
            <div
                v-for="company in getData()"
                :key="company.id"
                v-b-tooltip.hover
                :title="company.name"
                style="background-color: white"
            >
                <router-link :to="getCompanyLink(company.id)" style="text-decoration:none;">
                    <div
                        class="h-100 clickable px-2 py-2"
                        :class="[
                            'company-item',
                            isList ? 'company-list-item' : 'company-grid-item',
                            isList && 'pr-4',
                        ]"
                    >
                        <PulsatingDot
                            v-if="hasChatUsersActive(company.id)"
                            class="live-pulsator"
                            :text="$t('shared.chat.liveNow')"
                        />

                        <span v-if="company.prioritized" class="prio-banner">
                            PRIO
                        </span>

                        <b-img
                            class="image-default"
                            :src="company.logo"
                            fluid
                            style="grid-row: span 2; align-self: center; justify-self: center;"
                        />
                    </div>
                </router-link>
            </div>
        </div>

        <div v-if="isLoading" class="py-2">
            <ListItemSkeleton v-for="i in 4" :key="i" :rows="3" small />
        </div>

        <div v-else-if="data.length === 0">
            <slot />
        </div>
    </Paper>
</template>

<script>
// Services
import AnalyticService from '~/services/AnalyticService';

// Project components
import LayoutToggles from './LayoutToggles';
import ListItemSkeleton from '~/components/skeletons/ListItemSkeleton';
import PulsatingDot from '../components/miscellaneous/PulsatingDot';
import Checkbox from '~/components/builders/Base/Checkbox.vue';

export default {
    name: 'CompanyList',
    components: {
        PulsatingDot,
        LayoutToggles,
        ListItemSkeleton,
        Checkbox,
    },
    props: {
        label: {
            type: String,
            default: '',
        },
        withLayoutToggle: {
            type: Boolean,
            default: false,
        },
        isLoading: {
            type: Boolean,
            default: false,
        },
        data: {
            type: Array,
            default: () => [],
        },
        chatAvailability: {
            type: Array,
            default: () => [],
        },
        resolveRoute: {
            type: Function,
            default: undefined,
        },
        chatFilterButton: {
            type: Boolean,
            default: false,
        },
        forceList: {
            type: Boolean,
            default: false,
        },
    },
    data: () => ({
        isList: false,
        chatFilterToggle: false,
    }),
    methods: {
        getData() {
            if (this.chatFilterToggle) {
                return this.data.filter(item => {
                    return this.chatAvailability.filter(chatItem => chatItem.id === '' + item.id)
                        .length;
                });
            }
            return this.data;
        },
        getCompanyLink(id) {
            if (typeof this.resolveRoute !== 'undefined') return this.resolveRoute(id);
            else return this.$URLS.findcompanies.concat('/', id);
        },
        hasChatUsersActive(id) {
            return (
                this.chatAvailability.filter(item => item.id === '' + id && item.count > 0).length >
                0
            );
        },
    },
    mounted() {
        AnalyticService.impressionEvent(AnalyticService.IMPRESSION_ACTIONS.COMPANYLIST_VIEW_ACTION);
    },
};
</script>

<style lang="scss" scoped>
.company-list {
    span.toggle-text {
        position: relative;
        top: -2px;
        color: #777;
        font-size: 0.8em;
    }
}

.grid {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr 1fr;
    background-color: #f3f3f3;

    grid-gap: 1px;
}

.company-item {
    position: relative;

    &.company-list-item {
        display: grid;
        grid-template-rows: 50px 50px;
        grid-template-columns: repeat(auto);
        max-width: 100%;
        grid-column-gap: 1rem;
        grid-auto-flow: column;
    }
    &.company-grid-item {
        position: relative;

        display: grid;
        grid-template-rows: repeat(auto);
        grid-template-columns: 1fr 1fr;
        grid-auto-flow: row;
        grid-column-gap: 1rem;
        grid-row-gap: 0.2rem;
        min-height: 160px;

        text-align: center;
        overflow: hidden;
    }
    &.company-grid-item > *:nth-child(-n + 3) {
        grid-column: span 2;
    }
    &.company-grid-item > *:nth-child(2n + 5) {
        align-self: center;
        justify-self: center;
    }

    .live-pulsator {
        margin: 10px;
        font-size: 0.8rem;
    }
    span.prio-banner {
        position: absolute;
        top: 8px;
        right: 4px;

        width: 50%;
        padding: 0.2rem 0.5rem 0.1rem;

        transform: rotate(45deg) translate(25%, -100%);

        color: #fff;
        background-color: #d90c0c;
        box-shadow: -1px 1px 4px 1px rgba(0 0 0 / 0.35);

        font-size: 0.8rem;
        font-weight: 600;
        text-transform: uppercase;
    }
}

@media only screen and (max-width: 600px) {
    .grid {
        grid-template-columns: 1fr 1fr 1fr;
    }
    .company-list-item {
        grid-template-columns: 100px fit-content(100%) minmax(auto, 1fr) minmax(auto);
    }
    h5 {
        font-size: 14px;
    }
}

@media only screen and (max-width: 370px) {
    .grid {
        grid-template-columns: 1fr 1fr;
    }
}

.company h5 {
    align-self: end;
    margin-bottom: 0;
}
.company h6 {
    margin-bottom: 0;
}

.divider {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
}
.image-default {
    max-width: 90px !important;
}
</style>
